/** TanStack Query hooks for AI content generation. */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { BackgroundJob, GenerateContentRequest, StructureResponse } from '@formency/shared';
import { jobKeys } from './useJobs';

export const generationKeys = {
  all: ['f', 'generation'] as const,
  structure: (jobId: string) => [...generationKeys.all, 'structure', jobId] as const,
};

/** Optimistically patch a job's status in every cached job list. */
function optimisticJobStatus(queryClient: QueryClient, jobId: string, status: string) {
  queryClient.setQueriesData<BackgroundJob[]>({ queryKey: jobKeys.all }, (old) =>
    old?.map((j) => (j.id === jobId ? { ...j, status: status as BackgroundJob['status'] } : j))
  );
}

/** Start AI content generation (course/module/topic/lesson scope). */
export function useGenerateContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: GenerateContentRequest | FormData) => {
      if (request instanceof FormData) {
        return api.post<{ jobId: string; message: string }>('/generation/generate', request, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      return api.post<{ jobId: string; message: string }>('/generation/generate', request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
      queryClient.invalidateQueries({ queryKey: ['f', 'courses'] });
    },
  });
}

/** Cancel an in-progress generation job. */
export function useCancelGeneration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.post<{ jobId: string }>(`/generation/${jobId}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
      queryClient.invalidateQueries({ queryKey: ['f', 'courses'] });
    },
  });
}

/** Resume a failed or cancelled generation job. */
export function useResumeGeneration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.post<{ jobId: string }>(`/generation/${jobId}/resume`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
      queryClient.invalidateQueries({ queryKey: ['f', 'courses'] });
    },
  });
}

// ─── Structure review hooks ────────────────────────────────────────────────

/** Fetch the proposed structure for a job awaiting approval. */
export function useGenerationStructure(jobId: string | null) {
  return useQuery<StructureResponse>({
    queryKey: generationKeys.structure(jobId!),
    queryFn: () => api.get<StructureResponse>(`/generation/${jobId}/structure`),
    enabled: !!jobId,
  });
}

/** Refine the proposed structure with feedback (async — enqueues worker job). */
export function useRefineStructure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, feedback }: { jobId: string; feedback: string }) =>
      api.post<{ jobId: string }>(`/generation/${jobId}/refine`, { feedback }),
    onMutate: ({ jobId }) => {
      optimisticJobStatus(queryClient, jobId, 'processing');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
    onError: (_err, { jobId }) => {
      optimisticJobStatus(queryClient, jobId, 'awaiting_approval');
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

/** Approve the proposed structure and continue generation. */
export function useApproveStructure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.post<{ jobId: string }>(`/generation/${jobId}/approve`),
    onMutate: (jobId) => {
      optimisticJobStatus(queryClient, jobId, 'processing');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
      queryClient.invalidateQueries({ queryKey: ['f', 'courses'] });
    },
    onError: (_err, jobId) => {
      // Roll back optimistic update so the user sees the real status
      optimisticJobStatus(queryClient, jobId, 'awaiting_approval');
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

/** Discard the proposed structure and mark the job as cancelled. */
export function useDiscardStructure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.post(`/generation/${jobId}/discard`),
    onMutate: (jobId) => {
      optimisticJobStatus(queryClient, jobId, 'cancelled');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
      queryClient.invalidateQueries({ queryKey: ['f', 'courses'] });
    },
  });
}
